import React, {
  useContext,
  useEffect,
  useMemo,
} from 'react';
import clsx from 'clsx';
import Table from 'core/components/VirtualTable';
import {
  DrugIdentification,
  ServiceType,
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  authorizationAllowanceCPTCodeColumns,
  claimCPTCodesColumns,
  columnsByCodeType,
  customServiceColumns,
} from 'views/EMR/components/ServiceLookup/columns';
import omit from 'lodash/omit';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import {
  reorder,
} from 'utils/array';
import TableRow from 'core/components/VirtualTable/Row';
import {
  ServiceContext,
} from 'views/EMR/components/ServiceLookup/ServiceContext';
import DragNDropIcon from 'core/icons/dragNDrop';
import isNil from 'lodash/isNil';
import Chip from 'core/components/Chip';
import {
  ReadonlyContext,
} from 'core/context/readonly';
import {
  VisitNoteBillingCodeModifierDTO,
} from 'dtos/visitNoteBillingCodeModifierDTO';
import {
  VisitNoteBillingCodeLinkedICDCodeDTO,
} from 'dtos/visitNoteBillingCodeLinkedICDCodeDTO';
import type {
  ClaimServiceLineFormDTO,
} from 'dtos/claimServiceLineFormDTO';
import {
  roundNumber,
} from '@populateui/utils';
import {
  serviceLookupEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type Part = 'root' | 'body';

type Props = {
  className?: string;
  isClaim?: boolean;
  isAuthorizationAllowance?: boolean;
  classes?: Partial<Record<Part, string>>;
};

const calculateBilledAmount = (
  serviceCode: VisitNoteServiceCode,
  numberOfUnits: number,
) => {
  const {
    serviceCodeDetails,
    numberOfUnits: currentNumberOfUnits = 1,
  } = serviceCode;

  const currentPricePerUnit = (serviceCodeDetails?.billedAmount ?? 0) / currentNumberOfUnits;

  return numberOfUnits === 0
    ? serviceCodeDetails?.billedAmount
    : roundNumber(numberOfUnits * currentPricePerUnit);
};

type ServiceCodeLineUpdateValue =
  number
  | string
  | null
  | VisitNoteBillingCodeModifierDTO[]
  | VisitNoteBillingCodeLinkedICDCodeDTO[]
  | DrugIdentification
  | ClaimServiceLineFormDTO;

const allocateValue = (
  id: string,
  value: ServiceCodeLineUpdateValue,
  original: VisitNoteServiceCode,
) => {
  const newValue: Partial<VisitNoteServiceCode> = {};
  switch (id) {
    case 'ndc':
      newValue.serviceCodeDetails = {
        ...original.serviceCodeDetails,
        drugIdentification: {
          ...original.serviceCodeDetails?.drugIdentification,
          [id]: value as string ?? '',
        },
      };
      break;
    case 'lineNote':
      newValue.serviceCodeDetails = {
        ...original.serviceCodeDetails,
        [id]: value as string ?? '',
      };
      break;
    case 'drugIdentification':
      newValue.serviceCodeDetails = {
        ...original.serviceCodeDetails,
        drugIdentification: (value ?? {}) as DrugIdentification,
      };
      break;
    case 'numberOfUnits':
      newValue.numberOfUnits = Number(value) ?? 0;
      newValue.serviceCodeDetails = {
        ...original.serviceCodeDetails,
        billedAmount: calculateBilledAmount(original, Number(value)),
      };
      break;
    case 'patientAuthorization':
      newValue.claimServiceLine = {
        ...original.claimServiceLine,
        ...(value as ClaimServiceLineFormDTO),
      };
      break;
    case 'modifiers':
      newValue.modifiers = value as VisitNoteBillingCodeModifierDTO[];
      break;
    case 'linkedICDCodes':
      newValue.linkedICDCodes = value as VisitNoteBillingCodeLinkedICDCodeDTO[];
      break;
    default:
      newValue.serviceCodeDetails = {
        ...original.serviceCodeDetails,
        [id]: value ?? '',
      };
      break;
  }

  return {
    ...original,
    ...newValue,
  };
};

const tableClasses = {
  header: '!justify-start text-gray-600 !items-stretch !bg-gray-100',
  cell: '!h-auto !px-0.5 !py-1 !items-start',
  column: '!text-left',
  outerColumn: '!h-auto !items-end',
  scrollContainer: '!pb-0',
  outerRow: 'hover:border-b-[#dde2ee] hover:rounded-none hover:border-t-[#dde2ee] hover:!border-x-transparent transition-all',
};

const icdTableClasses = {
  outerRow: '!border-0 hover:!border-0 !min-h-8 transition-all',
  header: '!hidden',
  cell: '!py-1 !h-8',
};

export type ServiceCodeLineUpdateParams = {
  index: number;
  data: VisitNoteServiceCode;
  id: string;
  value: ServiceCodeLineUpdateValue;
};

type ServiceListColumnsParams = {
  type: ServiceType;
  isClaim: boolean;
  isAuthorizationAllowance: boolean;
  showCustomServiceCodePrice?: boolean;
  readonly: boolean;
};

function getColumns({
  type,
  isClaim,
  isAuthorizationAllowance,
  showCustomServiceCodePrice,
  readonly,
}: ServiceListColumnsParams) {
  const customCodeColumns = showCustomServiceCodePrice
    ? customServiceColumns
    : customServiceColumns.slice(0, 2);

  let billingCodeColumns = columnsByCodeType[type];

  if (isAuthorizationAllowance && type === 'CPTCode') {
    billingCodeColumns = authorizationAllowanceCPTCodeColumns;
  }

  if (isClaim) {
    billingCodeColumns = claimCPTCodesColumns;
  }

  const columnsByType = type === 'CustomCode'
    ? customCodeColumns
    : billingCodeColumns;

  return readonly
    ? columnsByType.map(({ meta, ...column }) => ({
      ...column,
      meta: omit(meta, 'editable'),
    }))
    : columnsByType;
}

export default function ServiceList({
  className,
  isClaim = false,
  isAuthorizationAllowance = false,
  classes: _classes,
}: Props) {
  const {
    value: selectedServices = [],
    type = 'CustomCode',
    onChange,
    getInstanceId,
    readonly = false,
    showCustomServiceCodePrice,
  } = useContext(ServiceContext);

  const updateSelectedServices = (
    index: number,
    id: string,
    value: ServiceCodeLineUpdateValue,
    original: VisitNoteServiceCode,
  ) => {
    const updated = allocateValue(id, value, original);
    onChange([
      ...selectedServices.slice(0, index),
      updated,
      ...selectedServices.slice(index + 1),
    ]);
  };

  useEffect(() => {
    const unsubscribe = serviceLookupEventChannel.on('onServiceCodeLineUpdate', ({
      value,
      id,
      index,
      data,
    }) => {
      if (type === 'CPTCode' && isClaim) {
        const updatedValue = allocateValue(id, value, data);
        onChange([
          ...selectedServices.slice(0, index),
          updatedValue,
          ...selectedServices.slice(index + 1),
        ]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [type, selectedServices]);

  const removeService = (index: number) => {
    onChange([
      ...selectedServices.slice(0, index),
      ...selectedServices.slice(index + 1),
    ]);
  };

  const columns = getColumns({
    type,
    isClaim,
    isAuthorizationAllowance,
    showCustomServiceCodePrice,
    readonly,
  });

  const onDragEnd = (result: DropResult) => {
    if (!isNil(result.destination)) {
      const items = reorder(
        selectedServices,
        result.source.index,
        result.destination.index,
      );

      onChange?.(items);
    }
  };

  const getClonedRow = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric,
  ) => {
    const { draggableProps, dragHandleProps, innerRef } = provided;
    const service = selectedServices[rubric.source.index];
    return (
      <div
        {...draggableProps}
        {...dragHandleProps}
        ref={innerRef}
        className="flex gap-2 items-center px-3 !max-h-[50px] opacity-90"
      >
        <DragNDropIcon className="!h-4 !w-4" />
        <Chip
          value={`${service?.code ?? service?.serviceName ?? ''}${type === 'ICDCode' ? ` - ${service?.description ?? ''}` : ''}`}
          disabled
          className="w-full truncate h-8"
          classes={{
            chip: clsx('h-full', {
              'w-[180px]': type === 'CustomCode',
              'w-[110px]': type === 'CPTCode',
            }),
          }}
        />
      </div>
    );
  };

  const contextValue = useMemo(() => ({ readonly }), [readonly]);

  const classes = type === 'ICDCode'
    ? { ...tableClasses, ...icdTableClasses, ..._classes }
    : { ...tableClasses, ..._classes };

  return (
    <div className={clsx(
      'populate-services-table flex flex-col gap-2',
      className,
    )}
    >
      <ReadonlyContext.Provider value={contextValue}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId={`${type}-droppable`}
            mode="virtual"
            renderClone={getClonedRow}
            isDropDisabled={readonly}
          >
            {(droppableProvided, droppableSnapshot) => (
              <div
                {...droppableProvided.droppableProps}
                ref={droppableProvided.innerRef}
                className="w-full"
              >
                <div className={clsx({ 'pb-2': !droppableSnapshot?.isDraggingOver })}>
                  <Table<VisitNoteServiceCode>
                    columns={columns}
                    data={selectedServices}
                    classes={{
                      ...classes,
                      outerRow: clsx(classes?.outerRow, {
                        'hover:!border-t-8 hover:!border-t-[#bfdbfe]': droppableSnapshot?.isDraggingOver,
                      }),
                      windowsClassName: 'w-full',
                      scrollbarGutter: 'inherit',
                      scrollContainer: clsx(classes?.scrollContainer, '!overflow-hidden'),
                    }}
                    defaultColumn={{
                      size: 100,
                      maxSize: 500,
                    }}
                    updateData={updateSelectedServices}
                    removeData={removeService}
                    renderRowComponent={(data) => {
                      const id = getInstanceId(data.original);
                      return (
                        <Draggable
                          key={id}
                          draggableId={id}
                          index={data.index}
                          isDragDisabled={readonly}
                        >
                          {(draggableProvided) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                              style={{
                                ...draggableProvided.draggableProps.style,
                                transform: 'none',
                              }}
                            >
                              <TableRow<VisitNoteServiceCode>
                                row={data}
                                className="!justify-start !items-stretch"
                                cellClassName={clsx('!items-start', classes.cell)}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    }}
                  />
                </div>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </ReadonlyContext.Provider>
    </div>
  );
}
